import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { DataGrid, type GridColDef, type GridRowId } from "@mui/x-data-grid";
import Radio from "@mui/material/Radio";

import { useNavigate } from "react-router-dom";
import { signatureservice } from "@/services/upload-service";
import { UploadSignatureModel } from "@/models/upload";

import {
  ROUTES,
  MODES,
  UI_TEXT,
  MESSAGES,
  CLASSES,
} from "@/constants/common";

/** API and Row interfaces */
type Row = {
  id: number | null;
  lanId: string;
  subCompanyCode: string;
  firstName: string;
  lastName: string;
  sigType: string;
};

const toRow = (api: UploadSignatureModel): Row => ({
  id: api.imageId,
  lanId: api.lanId,
  subCompanyCode: api.subCompanyCode,
  firstName: api.firstName,
  lastName: api.lastName,
  sigType: api.sigType,
});

const UploadSignatureSearch: React.FC = () => {
  const navigate = useNavigate();
  const [lanId, setLanId] = React.useState("");
  const [rows, setRows] = React.useState<Row[]>([]);
  const [selectedId, setSelectedId] = React.useState<GridRowId | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Keeping columns here (not in constants) per your request
  const columns: GridColDef<Row>[] = [
    {
      field: "__radio__",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const rowId = params.id;
        const checked = selectedId === rowId;
        return (
          <Radio
            size="small"
            checked={checked}
            onChange={() => setSelectedId(rowId)}
          />
        );
      },
    },
    { field: "lanId", headerName: "LAN ID", flex: 1, minWidth: 140 },
    { field: "subCompanyCode", headerName: "Sub Company Code", flex: 1, minWidth: 160 },
    { field: "firstName", headerName: "First Name", flex: 1, minWidth: 120 },
    { field: "lastName", headerName: "Last Name", flex: 1, minWidth: 120 },
    { field: "sigType", headerName: "Sig Type", flex: 1, minWidth: 120 },
  ];

  const handleRetrieve = async () => {
    setError(null);
    setSelectedId(null);
    setRows([]);

    if (!lanId.trim()) {
      setError(MESSAGES.ENTER_LAN_ID);
      return;
    }

    setLoading(true);
    try {
      const res = await signatureservice.getDetailByLanId(
        encodeURIComponent(lanId.trim())
      );
      const data = res?.data;
      if (!data) return;

      const list: UploadSignatureModel[] = Array.isArray(data) ? data : [data];
      setRows(list.map(toRow));
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.errorMessage ||
        err?.message ||
        MESSAGES.DEFAULT_FETCH_ERROR;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    navigate(ROUTES.SIGNATURE_FORM, { state: { mode: MODES.CREATE } });
  };

  const handleEdit = () => {
    if (!selectedId) return;
    navigate(ROUTES.SIGNATURE_FORM, {
      state: { selectedId: selectedId, mode: MODES.EDIT },
    });
  };

  const handleClone = () => {
    if (!selectedId) return;
    navigate(ROUTES.SIGNATURE_FORM, {
      state: { selectedId: selectedId, mode: MODES.CLONE },
    });
  };

  const primaryOrDisabled = (enabled: boolean) =>
    `${CLASSES.BTN_WIDE_BOLD} ${enabled ? CLASSES.BTN_PRIMARY : CLASSES.BTN_DISABLED}`;

  return (
    <div className={CLASSES.LAYOUT_WRAP}>
      <div className={CLASSES.INPUT_ROW}>
        <div className={CLASSES.FIELD_CONTAINER}>
          <Label>{UI_TEXT.LABEL_LAN_ID_REQUIRED}</Label>
          <Input value={lanId} onChange={(e) => setLanId(e.target.value)} />
        </div>

        <Button className={CLASSES.BTN_WIDE} onClick={handleRetrieve} disabled={loading}>
          {loading ? UI_TEXT.BTN_RETRIEVING : UI_TEXT.BTN_RETRIEVE}
        </Button>

        <Button className={CLASSES.BTN_WIDE} onClick={handleCreateNew}>
          {UI_TEXT.BTN_CREATE_NEW}
        </Button>
      </div>

      {error ? <div className={CLASSES.ERROR_TEXT}>{error}</div> : null}

      <div className={CLASSES.GRID_WRAPPER} style={{ height: 350 }}>
        <DataGrid<Row>
          rows={rows}
          columns={columns}
          density="compact"
          disableColumnMenu
          disableRowSelectionOnClick
          onRowClick={(params) => setSelectedId(params.id)}
          pagination
          pageSizeOptions={[5, 10, 25, 50, 100]}
          hideFooterSelectedRowCount
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#d7e7f6",
              fontWeight: 600,
            },
          }}
          loading={loading}
        />
      </div>

      <div className={CLASSES.BOTTOM_ROW}>
        <Button
          onClick={handleEdit}
          disabled={!selectedId}
          className={primaryOrDisabled(!!selectedId)}
        >
          {UI_TEXT.BTN_EDIT}
        </Button>

        <Button
          onClick={handleClone}
          disabled={!selectedId}
          className={primaryOrDisabled(!!selectedId)}
        >
          {UI_TEXT.BTN_CLONE}
        </Button>

        <Button
          onClick={() => navigate(ROUTES.HOME, { replace: true })}
          className={`${CLASSES.BTN_WIDE_BOLD} ${CLASSES.BTN_PRIMARY}`}
        >
          {UI_TEXT.BTN_BACK}
        </Button>
      </div>
    </div>
  );
};

export default UploadSignatureSearch;